import { Modal, Box, Text, Stack } from '@mantine/core'
import { Link } from 'react-router-dom'
import { IoCubeOutline } from 'react-icons/io5'
import type { BuildPartDto } from '../../app/models/build'

interface Props {
  zone: string;
  parts: BuildPartDto[];
  open: boolean;
  onClose: () => void;
}

export default function ZoneViewModal ({ zone, parts, open, onClose }: Props) {
  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={
        <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 18, color: 'var(--gs-text)' }}>
          {zone}
        </Text>
      }
      size="md"
      styles={{
        modal: {
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border)'
        },
        header: {
          background: 'var(--gs-surface)',
          borderBottom: '1px solid var(--gs-border)'
        },
        // Full-width on mobile
        '@media (max-width: 576px)': {
          modal: { margin: 0, borderRadius: 0, width: '100vw', maxWidth: '100vw' }
        } as never
      }}
    >
      <Stack spacing="sm">
        {parts.map((bp) => {
          // mainPhotoUrl is not populated in build details response (PartMainPhotoHelper
          // only runs in /parts list). Use imageUrl as primary photo source.
          const thumbUrl = bp.part?.imageUrl ?? null

          return (
            <Box
              key={bp.id}
              sx={{
                display: 'flex',
                gap: 12,
                padding: 12,
                background: 'var(--gs-surface2)',
                border: '1px solid var(--gs-border)',
                borderRadius: 8
              }}
            >
              {/* Thumbnail */}
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 6,
                  border: '1px solid var(--gs-border)',
                  background: 'var(--gs-bg)',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                {thumbUrl
                  ? (
                  <Box
                    component="img"
                    src={thumbUrl}
                    alt={bp.part?.name ?? 'Part'}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                    )
                  : (
                  <IoCubeOutline size={24} color="var(--gs-muted)" />
                    )}
              </Box>

              {/* Part info */}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Text size="sm" weight={600} sx={{ color: 'var(--gs-text)' }} lineClamp={1}>
                  {bp.part?.name ?? 'Part'}
                </Text>
                {bp.part?.manufacturer && (
                  <Text size="xs" sx={{ color: 'var(--gs-muted)' }}>
                    {bp.part.manufacturer}
                  </Text>
                )}
                {bp.part?.partNumber && (
                  <Text
                    size="xs"
                    sx={{ color: 'var(--gs-muted)', fontFamily: 'ui-monospace, monospace' }}
                  >
                    {bp.part.partNumber}
                  </Text>
                )}
                <Text
                  component={Link}
                  to={`/parts/${bp.partId}`}
                  size="xs"
                  sx={{
                    color: 'var(--gs-accent)',
                    textDecoration: 'none',
                    display: 'inline-block',
                    marginTop: 4,
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  View in Parts Bin →
                </Text>
              </Box>
            </Box>
          )
        })}
      </Stack>
    </Modal>
  )
}
